/**
 * The page displaying the specific history of AI Music Detector tasks.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import {
  IampHeaderIconTitle,
  IampTableColumnHeader,
} from '@/components/IampTable'
import '@/components/IampTable.css'
import '@/components/StatusComp.css'
import { JobDetail, JobUtil } from '@amplify3/duplo-back-end'
import { useEffect, useState } from 'react'
import { AiOutlineAreaChart } from 'react-icons/ai'
import './AimdTaskList.css'
import AimdTimeAnalysisButton from './AimdTimeAnalysisButton'
import './TaskList.css'
import { TaskUtil } from './TaskUtil'

const DebugTaskList = false
const milliSecondsInDay: number = 1000 * 60 * 60 * 24
const TaskDayCountMax = 7

interface AimdTaskList {
  taskList: JobDetail[]
  lastTaskId?: string
  requestMoreTasks: () => Promise<void>
}

const AimdTaskList = (props: AimdTaskList) => {
  const now = new Date()

  // Define class name with debug management
  const completeClassName = `aimd-task-list${DebugTaskList ? ' debug' : ''}`
  const [isLoadingMore, setLoadingMore] = useState(false)

  function isTaskToDisplay(task: JobDetail) {
    const dayCount =
      Math.floor(now.getTime() - new Date(task.job_start_datetime).getTime()) /
      milliSecondsInDay
    return dayCount < TaskDayCountMax
  }

  useEffect(() => {
    // Task list has changed: set the state `isLoading` to false
    // meaning change is completed.
    setLoadingMore(false)
  }, [props.taskList])

  /**
   * Handle the click on the button "Load More".
   *
   * The state `isLoading` is set to true. It will be set to false
   * in a `useEffect` when the prop `taskList` will change.
   */
  const onLoadMoreClick = async () => {
    if (!isLoadingMore) {
      setLoadingMore(true)
      await props.requestMoreTasks()
    }
  }

  return (
    <div className={completeClassName}>
      <table className="aimd-task-table iamp-table">
        <thead>
          <tr>
            {AimdTaskTableHeaderList.map((header: IampTableColumnHeader) => (
              <IampHeaderIconTitle key={header.title} {...header} />
            ))}
          </tr>
        </thead>

        <tbody>
          {Object.values(props.taskList).map(
            (task: JobDetail, index: number) =>
              isTaskToDisplay(task) && (
                <AimdTaskTableRow key={task.job_id} task={task} index={index} />
              )
          )}
        </tbody>
      </table>

      {props.taskList.length !== undefined &&
        props.taskList.length > 0 &&
        (props.lastTaskId !== undefined || isLoadingMore) &&
        isTaskToDisplay(props.taskList[props.taskList.length - 1]) && (
          <button
            type="button"
            id="LoadMoreButton"
            className="button-dark"
            disabled={isLoadingMore}
            onClick={onLoadMoreClick}
          >
            <div className="button-label">
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </div>
          </button>
        )}
    </div>
  )
}

/**
 * The list of column headers of the task table.
 */
const AimdTaskTableHeaderList: IampTableColumnHeader[] = [
  {
    className: 'column-file-name',
    title: 'File name',
  },
  {
    className: 'ai-percent',
    title: 'AI Probability',
  },
  {
    className: 'model',
    title: 'Model',
  },
  {
    className: 'date',
    title: 'Date',
  },
  {
    className: 'status',
    title: 'Status',
  },
  {
    className: 'column-additional-info',
    title: '',
  },
]

interface AimdTaskTableRowProps {
  task: JobDetail
  index: number
}

const AimdTaskTableRow = (props: AimdTaskTableRowProps) => {
  /**
   * Get the value of AI Probability, depending on the version of AIMD.
   * @param result The result data of AIMD. Should be one item
   *    of JobDetail.report_info.resultList
   * @returns The AI probability.
   */
  function getAiProbability(result: any): number | undefined {
    if (result.aiProbability !== undefined) {
      // AIMD v2
      return result.aiProbability
    }

    if (result.confidence !== undefined && result.isAi !== undefined) {
      // AIMD v1
      if (result.isAi) {
        return result.confidence
      } else {
        return 100 - result.confidence
      }
    }

    // Result data not recognized
    return undefined
  }

  /**
   * Format the contents of the AI Probability cell in the table.
   * @param result The result data of AIMD. Should be one item
   *    of JobDetail.report_info.resultList
   * @returns The text to display in the table cell.
   */
  function formatAiProbability(result: any): string {
    const value = getAiProbability(result)

    if (value !== undefined) {
      return `${value}%`
    }

    return ''
  }

  return (
    <>
      {(props.task.report_info?.resultList
        ? Object.values(props.task.report_info.resultList)
        : [ // No report or result: could be detection running or error.
            {
              inputFilename:
                props.task.job_status === 'error'
                  ? 'Detector error'
                  : 'Detection running...',
            },
          ]
      ).map((result: any, inputFileIndex: number) => {
        if (!result) {
          return null
        }

        const redCharClassName =
          Number(result.aiProbability) > 50.0 ? 'red-chars' : ''

        return (
          <tr key={`${props.task.job_id}-${result.id ?? inputFileIndex}`}>
            <td
              className={`column-file-name truncate ${props.task.job_status}`}
            >
              <div className="column-file-name-content">
                <div className="file-name truncate">{result.inputFilename}</div>
                <div className="job-id">
                  ID: {JobUtil.getTaskName(props.task)}
                </div>
              </div>
            </td>
            <td className={`ai-percent ${redCharClassName}`}>
              {formatAiProbability(result)}
            </td>
            <td className={`model ${redCharClassName}`}>
              {result.suspectedModel}
            </td>
            <td className={`date ${props.task.job_status}`}>
              {new Date(props.task.job_start_datetime).toLocaleString()}
            </td>
            <td className="status auto-width">
              <AimdTaskStatus status={props.task.job_status} />
            </td>
            <td className="column-additional-info">
              <div className="additional-info-container">
                {result.timeAnalysis && (
                  <AimdTimeAnalysisButton
                    icon={AiOutlineAreaChart}
                    jobResult={props.task}
                    inputFileIndex={inputFileIndex}
                    showOnlyOneAnalysis={true}
                  />
                )}
              </div>
            </td>
          </tr>
        )
      })}
    </>
  )
}

interface AimdTaskStatusProps {
  status: string
}

const AimdTaskStatus = (props: AimdTaskStatusProps) => (
  <div className={`status-comp aimd-task-status ${props.status}`}>
    {TaskUtil.jobStatusToFormattedString(props.status)}
  </div>
)

export default AimdTaskList
